<template>
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" height="38.4mm" width="38.4mm">
        <defs>
            <radialGradient :id="uid+'-face'" cx="50%" cy="50%" r="50%">
                <stop offset="0%"   stop-color="#eaeaee" />
                <stop offset="65%"  stop-color="#d0d0d8" />
                <stop offset="100%" stop-color="#b4b4c0" />
            </radialGradient>
            <radialGradient :id="uid+'-glow'" cx="50%" cy="50%" r="50%">
                <stop offset="55%" stop-color="#c8d8ff" stop-opacity="0" />
                <stop offset="100%" stop-color="#c8d8ff" :stop-opacity="glowOpacity" />
            </radialGradient>
        </defs>

        <!-- Soft halo scales with illumination -->
        <ellipse v-if="illumination > 0.05" cx="16" cy="16" rx="15" ry="15"
            :fill="`url(#${uid}-glow)`" />

        <!-- Entire disc rotated by parallactic angle so phase orientation matches the sky -->
        <g :transform="`rotate(${-parallacticAngle}, 16, 16)`">
            <!-- Unlit dark base -->
            <circle cx="16" cy="16" r="14" fill="#1a1a2e" />
            <!-- Lit region on top -->
            <path v-if="litPath" :d="litPath" :fill="`url(#${uid}-face)`" />
            <!-- Limb -->
            <circle cx="16" cy="16" r="14" fill="none" stroke="#9090a0" stroke-width="0.7" />
        </g>
    </svg>
</template>

<script>
let _uid = 0

export default {
    name: 'MoonGraph',
    props: {
        illumination:  { type: Number, default: 0 },
        moonPhaseName: { type: String, default: '' },
        altitude:      { type: Number, default: null },
        azimuth:       { type: Number, default: null },
        lat:           { type: Number, default: null },
    },
    data() {
        return { uid: 'mg-' + (++_uid) }
    },
    computed: {
        // Phases after full moon are waning; "Last Quarter" doesn't contain "waning" so check it explicitly
        isWaxing() {
            const name = (this.moonPhaseName || '').toLowerCase()
            return !name.includes('waning') && !name.includes('last quarter')
        },
        glowOpacity() {
            return this.illumination * 0.38
        },
        // Parallactic angle: angle between zenith direction and celestial north at the moon's
        // position, derived entirely from the moon's alt/az and the observer's latitude.
        // Positive = moon in the east (rising); negative = moon in the west (setting).
        parallacticAngle() {
            if (this.altitude === null || this.azimuth === null || this.lat === null) return 0
            const φ = this.lat   * Math.PI / 180
            const a = this.altitude * Math.PI / 180
            const A = this.azimuth  * Math.PI / 180   // compass bearing: 0=N, 90=E
            const sinDec = Math.sin(φ)*Math.sin(a) + Math.cos(φ)*Math.cos(a)*Math.cos(A)
            const dec    = Math.asin(Math.max(-1, Math.min(1, sinDec)))
            const cosDec = Math.cos(dec)
            const cosA   = Math.cos(a)
            if (Math.abs(cosDec) < 0.001 || Math.abs(cosA) < 0.001) return 0
            const sinQ = Math.cos(φ) * Math.sin(A) / cosDec
            const cosQ = (Math.sin(φ) - Math.sin(a)*sinDec) / (cosA * cosDec)
            return Math.atan2(sinQ, cosQ) * 180 / Math.PI
        },
        // SVG path for the lit region of the moon drawn on top of the dark base disc.
        // Waxing: right half lit. Waning: left half lit.
        // Terminator ellipse x-radius = r * |1 − 2f|; sweep flags determine bulge direction.
        litPath() {
            const r = 14
            const f = this.illumination
            if (f <= 0.001) return ''
            if (f >= 0.999) return `M 16 2 A ${r} ${r} 0 0 1 16 30 A ${r} ${r} 0 0 1 16 2 Z`
            const tx = Math.max(0.1, r * Math.abs(1 - 2 * f))
            // ts: terminator arc sweep; ss: semicircle arc sweep
            const ts = (f < 0.5 && this.isWaxing) || (f >= 0.5 && !this.isWaxing) ? 1 : 0
            const ss = (this.isWaxing && f >= 0.5) ? 0 : 1
            return `M 16 2 A ${tx} ${r} 0 0 ${ts} 16 30 A ${r} ${r} 0 0 ${ss} 16 2 Z`
        },
    },
}
</script>
